import { useState, type FormEvent } from 'react';
import type { StepWarning, TestStep } from '../api/tests';
import { ca } from '../i18n/ca';
import { freeStepId, parseSteps } from './testSteps';

/**
 * What the platform's reading of a step's wording warns about, under the step:
 * an admin's, and only a warning. A step is one question a key answers in game
 * (F10 yes, F11 no, F8 not seen), so two facts, a condition or a negative
 * question can make a right observation a wrong answer (the owner, 2026-10-12).
 */
export function StepWarnings({ warnings }: { warnings: StepWarning[] | undefined }) {
  const t = ca.testsPage;
  if (!warnings || warnings.length === 0) return null;
  return (
    <ul className="step-warnings" aria-label={t.stepWarningsTitle}>
      {warnings.map((w) => (
        <li key={w}>
          <i className="bi bi-exclamation-triangle" aria-hidden="true" /> {t.stepWarnings[w] ?? w}
        </li>
      ))}
    </ul>
  );
}

/**
 * An admin adds a step to a card nobody has taken, in the create form's own
 * line format, after the step chosen or at the end: how a compound step is
 * split without making the card again.
 */
export function AddStep({
  steps,
  onAdd,
}: {
  steps: TestStep[];
  onAdd: (step: TestStep, after?: string) => Promise<unknown>;
}) {
  const t = ca.testsPage;
  const [line, setLine] = useState('');
  const [after, setAfter] = useState('');

  function submit(event: FormEvent) {
    event.preventDefault();
    const firstLine = line.split('\n').find((l) => l.trim()) ?? '';
    const [parsed] = parseSteps(firstLine);
    if (!parsed || !parsed.text) return;
    // parseSteps numbers a line with no id from its place in the text: here the card decides.
    const explicit = /^[a-z0-9_]{1,32}\s*\|/.test(firstLine.trim());
    const step = explicit ? parsed : { ...parsed, id: freeStepId(steps) };
    void onAdd(step, after || undefined).then(() => setLine(''));
  }

  return (
    <form className="step-add" onSubmit={submit} aria-label={t.stepAdd}>
      <label>
        {t.stepAdd}
        <input value={line} onChange={(e) => setLine(e.target.value)} required />
      </label>
      <label>
        {t.stepAddAfter}
        <select value={after} onChange={(e) => setAfter(e.target.value)}>
          <option value="">{t.stepAddLast}</option>
          {steps.map((s, i) => (
            <option key={s.id} value={s.id}>
              {i + 1} · {s.id}
            </option>
          ))}
        </select>
      </label>
      <p className="muted small">{t.stepAddHint}</p>
      <p className="row actions">
        <button type="submit">{t.stepAdd}</button>
      </p>
    </form>
  );
}
